import { plain } from '../../lib/labels';
import { UserName } from '../../lib/listenerName';
import { Alert, Card, Descriptions, Empty, Space, Table, Tag, Timeline, Tooltip, Typography } from 'antd';
import { formatRupees, ledgerReasonLabel } from '../../lib/money';
import {
  FAILURE_LABEL,
  PROVIDER_LABEL,
  STAGE_LABEL,
  STATUS_COLOR,
  STATUS_HELP,
  STATUS_LABEL,
  paymentVerdict,
  reasonCodeText,
  stageColor,
  type PaymentDetail,
  type TimelineEntry,
} from './paymentLifecycle';

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function yesNo(v: boolean, yes = 'Yes', no = 'No') {
  return <Tag color={v ? 'success' : 'default'}>{v ? yes : no}</Tag>;
}

const OUTCOME: Record<PaymentDetail['investigation']['providerOutcome'], [string, string]> = {
  accepted: ['Accepted', 'success'],
  rejected: ['Rejected', 'error'],
  cancelled: ['Cancelled by user', 'volcano'],
  unknown: ['Unknown / no result', 'default'],
};
const VERIFICATION: Record<PaymentDetail['investigation']['verification'], [string, string]> = {
  succeeded: ['Succeeded', 'success'],
  failed: ['Failed', 'error'],
  not_reached: ['Not reached', 'default'],
};

function TimelineItem({ e }: { e: TimelineEntry }) {
  const title =
    e.kind === 'provider_event'
      ? `${e.stageLabel ?? 'Provider event'} · ${e.eventType}`
      : (e.stageLabel ?? e.eventType);
  const meta = e.metadata ?? {};
  return (
    <div data-testid="timeline-item">
      <Typography.Text code style={{ fontSize: 11 }}>
        {clock(e.at)}
      </Typography.Text>{' '}
      <Typography.Text strong>{title}</Typography.Text>
      {e.source && (
        <Tag style={{ marginInlineStart: 6, fontSize: 10 }} bordered={false}>
          {e.source}
        </Tag>
      )}
      {e.status && (
        <Tag color={STATUS_COLOR[e.status]} style={{ fontSize: 10 }}>
          {STATUS_LABEL[e.status]}
        </Tag>
      )}
      {meta.duplicate === true && (
        <Tag color="gold" style={{ fontSize: 10 }}>
          duplicate — no second credit
        </Tag>
      )}
      {meta.ignored === true && (
        <Tag style={{ fontSize: 10 }}>ignored</Tag>
      )}
      {e.reason && <div style={{ fontSize: 12, opacity: 0.85 }}>{e.reason}</div>}
      {e.failureCode && (
        <div style={{ fontSize: 12 }}>
          Reason: <Typography.Text code>{reasonCodeText(e.failureCode)}</Typography.Text>
        </div>
      )}
      {e.providerErrorCode && (
        <div style={{ fontSize: 12 }}>
          Provider code: <Typography.Text code>{e.providerErrorCode}</Typography.Text>
        </div>
      )}
      {e.processingError && (
        <div style={{ fontSize: 12 }}>
          <Typography.Text type="danger">Processing error: {e.processingError}</Typography.Text>
        </div>
      )}
    </div>
  );
}

/**
 * One recharge attempt, fully explained (Recharges page → "Open").
 *
 * Laid out in the order an operator asks the questions: the verdict; who, what
 * and how much; where it stopped and why; what the provider said; the
 * investigation answers (accepted? verified? webhook? credited — per the
 * ledger?); then the full chronological timeline and the ledger proof.
 */
export function PaymentDetailView({ payment: p }: { payment: PaymentDetail }) {
  const verdict = paymentVerdict(p);
  const inv = p.investigation;
  const pe = p.providerError;
  const hasProviderError = Boolean(pe.code || pe.reason || pe.message || pe.source || pe.step);
  const coinsTotal = (p.paidCoins ?? 0) + p.bonusCoins;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert type={verdict.type} showIcon message={verdict.text} data-testid="payment-verdict" />

      <Descriptions size="small" column={2} bordered title="Payment">
        <Descriptions.Item label="Payment ID" span={2}>
          <Typography.Text code copyable style={{ fontSize: 11 }}>
            {p.id}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="User">
          <UserName handle={p.user.handle} displayName={p.user.displayName} userId={p.user.id} />{' '}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {p.user.id.slice(0, 8)}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Package">{p.package?.name ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Service">
          {PROVIDER_LABEL[p.provider]}
          {p.ucb && (
            <Tooltip title="India User Choice Billing: chosen in Google Play's chooser; reported to Google.">
              <Tag style={{ marginInlineStart: 6 }}>UCB</Tag>
            </Tooltip>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Amount">
          {formatRupees(p.amountPaise)} {p.currency}
        </Descriptions.Item>
        <Descriptions.Item label="Paid coins">{p.paidCoins?.toLocaleString('en-IN') ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Bonus coins">
          {p.bonusCoins.toLocaleString('en-IN')}
          {p.paidCoins != null && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {' '}
              (total {coinsTotal.toLocaleString('en-IN')})
            </Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Payment status">
          <Tooltip title={STATUS_HELP[p.status]}>
            <Tag color={STATUS_COLOR[p.status]}>{STATUS_LABEL[p.status]}</Tag>
          </Tooltip>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {p.status}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Step reached">
          <Tag color={stageColor(p.stage)}>{STAGE_LABEL[p.stage] ?? p.stageLabel}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Reason">
          {p.failureCode ? (
            <>
              <Typography.Text code>{reasonCodeText(p.failureCode)}</Typography.Text>{' '}
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {FAILURE_LABEL[p.failureCode]}
              </Typography.Text>
            </>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Failure reason">{p.failureReason ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Payment company transaction ID">
          {p.providerTransactionId ? <Typography.Text code copyable>{p.providerTransactionId}</Typography.Text> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Payment company order ID">
          {p.providerOrderId ? (
            <Typography.Text code copyable>{p.providerOrderId}</Typography.Text>
          ) : (
            <Typography.Text type="secondary">not assigned yet</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Payment company payment ID" span={2}>
          {p.providerPaymentId ? <Typography.Text code copyable>{p.providerPaymentId}</Typography.Text> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Created at">{when(p.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="Updated at">{when(p.updatedAt)}</Descriptions.Item>
        <Descriptions.Item label="Paid (verified) at">{when(p.paidAt)}</Descriptions.Item>
        <Descriptions.Item label="Completed (credited) at">{when(p.completedAt)}</Descriptions.Item>
        <Descriptions.Item label="Failed / closed at">{when(p.closedAt)}</Descriptions.Item>
        {p.provider === 'razorpay' && p.ucb ? (
          <Descriptions.Item label="Reported to Google">{when(p.googleReportedAt)}</Descriptions.Item>
        ) : (
          <Descriptions.Item label=" ">{null}</Descriptions.Item>
        )}
      </Descriptions>

      <Card size="small" title="Provider error">
        {hasProviderError ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Payment company error code">{pe.code ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Payment company reason">{pe.reason ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Payment company message">{pe.message ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Where it failed">
              {pe.source ?? '—'}
              {pe.step ? ` · ${pe.step}` : ''}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">The provider reported no error for this attempt.</Typography.Text>
        )}
      </Card>

      <Card size="small" title="Investigation">
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Payment company accepted?">
            <Tag color={OUTCOME[inv.providerOutcome][1]}>{OUTCOME[inv.providerOutcome][0]}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Checked by our system">
            <Tag color={VERIFICATION[inv.verification][1]}>{VERIFICATION[inv.verification][0]}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Payment company confirmed?">
            {yesNo(inv.webhookReceived)}
            {inv.webhookCount > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {inv.webhookCount} deliver{inv.webhookCount === 1 ? 'y' : 'ies'}
                {inv.webhookErrors > 0 ? `, ${inv.webhookErrors} with processing errors` : ''}
              </Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Coins added to wallet?">
            {yesNo(inv.coinsCredited)}
            {inv.coinsCredited && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {inv.paidCoinsCredited} paid + {inv.bonusCoinsCredited} bonus
              </Typography.Text>
            )}
            {inv.creditPending && <Tag color="magenta">credit owed</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title={`Payment timeline (${p.timeline.length})`}>
        {p.timeline.length === 0 ? (
          <Empty description="No lifecycle events recorded (attempt predates tracking)." />
        ) : (
          <Timeline
            items={p.timeline.map((e) => ({
              key: e.id,
              color: e.processingError ? 'red' : stageColor(e.stage),
              children: <TimelineItem e={e} />,
            }))}
          />
        )}
      </Card>

      <Card size="small" title={`Ledger (${p.ledger.length})`}>
        {p.ledger.length === 0 ? (
          <Empty description="No ledger rows — no coins moved for this attempt." />
        ) : (
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={p.ledger}
            columns={[
              { title: 'When', dataIndex: 'at', render: (s: string) => when(s), width: 170 },
              { title: 'Account', dataIndex: 'account', width: 170 },
              {
                title: 'In / out',
                dataIndex: 'direction',
                render: (d: string) => <Tag color={d === 'credit' ? 'green' : 'default'}>{plain(d)}</Tag>,
                width: 70,
              },
              {
                title: 'Reason',
                dataIndex: 'reasonCode',
                width: 170,
                render: (c: string, l: PaymentDetail['ledger'][number]) => (
                  <span title={c}>{ledgerReasonLabel(c, l.direction)}</span>
                ),
              },
              {
                title: 'Amount',
                key: 'amt',
                render: (_: unknown, l: PaymentDetail['ledger'][number]) =>
                  l.amountCoins != null ? `${l.amountCoins} coins` : formatRupees(l.amountPaise ?? '0'),
                width: 110,
              },
              {
                title: 'Request reference',
                dataIndex: 'idempotencyKey',
                render: (k: string) => <code style={{ fontSize: 10 }}>{k}</code>,
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}
